/**
 * @module engine3d/solid/stl
 * @summary Writes the evaluated part out as a binary STL.
 * @description
 *   The one export that makes the modeller more than a picture: an STL opens in
 *   SolidWorks, Inventor, Fusion, FreeCAD, a slicer or a quoting package, so a
 *   hopper drawn here can be sent to whoever is actually cutting the plate.
 *
 *   Binary rather than ASCII — a part of forty thousand triangles is about two
 *   megabytes binary against roughly fifteen as text, and every consumer reads
 *   binary.
 *
 *   UNITS: STL carries no unit declaration; by near-universal convention the
 *   figures are millimetres, so the scene's world units are converted back on
 *   the way out. A part modelled as 1600 mm wide therefore imports as 1600 mm.
 */
import type { Scene } from '../scene';

/** The 80-byte header. Consumers ignore it, but a name makes the file legible. */
function header(name: string): Uint8Array {
  const h = new Uint8Array(80);
  const text = `JCC PFD Engineering Tool - ${name}`.slice(0, 79);
  for (let i = 0; i < text.length; i++) h[i] = text.charCodeAt(i) & 0x7f;
  return h;
}

/**
 * Binary STL of every triangle in the scene, as a raw buffer.
 * Split out from `sceneToStl` so the self-check can read the facets straight
 * back without a Blob and an await.
 * @param mmPerUnit World units back to millimetres.
 */
export function sceneToStlBuffer(scene: Scene, name: string, mmPerUnit: number): ArrayBuffer {
  const n = scene.count;
  const buf = new ArrayBuffer(84 + n * 50);
  const bytes = new Uint8Array(buf);
  bytes.set(header(name), 0);
  const view = new DataView(buf);
  view.setUint32(80, n, true);

  for (let t = 0; t < n; t++) {
    const o = 84 + t * 50;
    const p = t * 9;
    const ax = scene.pos[p] * mmPerUnit;
    const ay = scene.pos[p + 1] * mmPerUnit;
    const az = scene.pos[p + 2] * mmPerUnit;
    const bx = scene.pos[p + 3] * mmPerUnit;
    const by = scene.pos[p + 4] * mmPerUnit;
    const bz = scene.pos[p + 5] * mmPerUnit;
    const cx = scene.pos[p + 6] * mmPerUnit;
    const cy = scene.pos[p + 7] * mmPerUnit;
    const cz = scene.pos[p + 8] * mmPerUnit;

    // The facet normal is recomputed from the winding rather than copied from
    // the vertex normals: STL's normal must agree with the vertex order, and
    // the smooth per-vertex normals this renderer carries deliberately do not.
    const ux = bx - ax;
    const uy = by - ay;
    const uz = bz - az;
    const vx = cx - ax;
    const vy = cy - ay;
    const vz = cz - az;
    let nx = uy * vz - uz * vy;
    let ny = uz * vx - ux * vz;
    let nz = ux * vy - uy * vx;
    const l = Math.hypot(nx, ny, nz);
    if (l > 1e-12) {
      nx /= l;
      ny /= l;
      nz /= l;
    } else {
      nx = 0;
      ny = 0;
      nz = 0;
    }

    // STL is Z-up, this renderer is Y-up: (x, y, z) → (x, -z, y). That is a
    // proper rotation, so the normal takes exactly the same swap — applying it
    // to the vertices alone would ship a file whose facets all point inward.
    view.setFloat32(o, nx, true);
    view.setFloat32(o + 4, -nz, true);
    view.setFloat32(o + 8, ny, true);
    const put = (k: number, x: number, y: number, z: number) => {
      view.setFloat32(o + k, x, true);
      view.setFloat32(o + k + 4, -z, true);
      view.setFloat32(o + k + 8, y, true);
    };
    put(12, ax, ay, az);
    put(24, bx, by, bz);
    put(36, cx, cy, cz);
    view.setUint16(o + 48, 0, true);
  }

  return buf;
}

/** The same file, wrapped for download. */
export function sceneToStl(scene: Scene, name: string, mmPerUnit: number): Blob {
  return new Blob([sceneToStlBuffer(scene, name, mmPerUnit)], { type: 'model/stl' });
}
